import type { ButtonProps } from "@braintrust/braintrust-ui-components";
import { Box, Button } from "@braintrust/braintrust-ui-components";
import { submitProposalClicked } from "@js/apps/jobs/actions";
import {
  PendingBankingInfoButton,
  RespondToOfferButton,
  ReviewContractButton,
  ScheduleInterviewButton,
  StartAIInterviewButton,
  ViewApplicationButton,
} from "@js/apps/jobs/components/job-action-buttons/components";
import { RouterLink } from "@js/components/link";
import { useAppDispatch } from "@js/hooks";
import type {
  FreelancerBidListItem,
  Job,
  MainJobListingJob,
} from "@js/types/jobs";
import { getURLWithBackLink } from "@js/utils";

export type BidButtonProps = {
  job: Job | MainJobListingJob;
  bid?: FreelancerBidListItem;
  openInNewTab?: boolean;
} & Optional<ButtonProps, "variant">;

export const BidButton = ({
  job,
  bid,
  variant = "primary",
  openInNewTab,
  ...props
}: BidButtonProps) => {
  const dispatch = useAppDispatch();
  if (!job) return null;

  const offerStatus = bid?.current_offer?.status;
  const statusForFreelancer = job.status_for_freelancer;

  switch (statusForFreelancer) {
    case ENUMS.JobFreelancerStatus.PENDING_BANK_INFORMATION:
      return <PendingBankingInfoButton variant="primary" {...props} />;
    case ENUMS.JobFreelancerStatus.JOB_FILLED:
    case ENUMS.JobFreelancerStatus.JOB_CLOSED:
    case ENUMS.JobFreelancerStatus.JOB_ON_HOLD:
    case ENUMS.JobFreelancerStatus.PENDING_VERIFICATION:
    case ENUMS.JobFreelancerStatus.PENDING_APPROVAL:
    case ENUMS.JobFreelancerStatus.APPLICATION_REJECTED:
    case ENUMS.JobFreelancerStatus.IN_REVIEW_AFTER_AI_INTERVIEW:
      if (!job.has_freelancer_bid || !bid) return null;

      return (
        <ViewApplicationButton
          jobId={job.id}
          bidId={bid.id}
          variant="primary"
          {...props}
        />
      );
    case ENUMS.JobFreelancerStatus.LIVE_INTERVIEWING:
      return (
        <ScheduleInterviewButton
          employerUserId={job.creator_user_id}
          variant="primary"
          {...props}
        />
      );
    case ENUMS.JobFreelancerStatus.AI_INTERVIEWING:
      if (!bid) return null;

      return (
        <StartAIInterviewButton variant="primary" bidId={bid.id} {...props} />
      );
    case ENUMS.JobFreelancerStatus.OFFER_ACCEPTED:
      if (!bid?.current_offer?.id) return null;

      return (
        <ReviewContractButton
          jobId={job.id}
          offerId={bid.current_offer.id}
          variant="primary"
          {...props}
        />
      );
    case ENUMS.JobFreelancerStatus.NEW_OFFER:
    case ENUMS.JobFreelancerStatus.UPDATED_OFFER:
      if (!bid?.current_offer?.id) {
        return null;
      }

      return (
        <RespondToOfferButton
          jobId={job.id}
          offerId={bid.current_offer.id}
          variant="primary"
          {...props}
        />
      );
    case ENUMS.JobFreelancerStatus.APPLICATION_SENT:
    case ENUMS.JobFreelancerStatus.IN_REVIEW:
      if (!bid) return null;

      return (
        <>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <ViewApplicationButton
              variant={variant}
              jobId={job.id}
              bidId={bid.id}
              target={openInNewTab ? "_blank" : undefined}
              {...props}
            />
          </Box>
        </>
      );
    case ENUMS.JobFreelancerStatus.ACCEPTING_APPLICATIONS:
    case ENUMS.JobFreelancerStatus.INVITED_BY_MATCHER:
    case ENUMS.JobFreelancerStatus.INVITED_BY_CLIENT:
    case ENUMS.JobFreelancerStatus.OFFER_EXTENDED:
    case ENUMS.JobFreelancerStatus.NON_SUPPORTED_LOCATION:
    case undefined:
    default:
      if (
        bid?.current_offer?.id &&
        offerStatus === ENUMS.OfferStatus.REJECTED
      ) {
        return (
          <ViewApplicationButton
            jobId={job.id}
            bidId={bid.current_offer.id}
            variant="secondary"
          />
        );
      }

      if ("can_bid" in job && !job.can_bid) {
        return null;
      }

      return (
        <Button
          variant={variant}
          target={openInNewTab ? "_blank" : undefined}
          {...props}
          onClick={() => {
            dispatch(
              submitProposalClicked({ job, search: window.location.search }),
            );
          }}
          to={getURLWithBackLink(`/jobs/${job.id}/proposals/new/`)}
          RouterLink={RouterLink}
        >
          Apply
        </Button>
      );
  }
};
